'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useId } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { resetPassword } from '@eliadmin/core/src/api/customers/customers-api';
import { FormAlert } from '@eliadmin/core/src/components/dlz/auth/password-reset/form-alert';
import { RESET_COPY } from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-copy';
import {
  resetPasswordSchema,
  type ResetPasswordValues,
} from '@eliadmin/core/src/components/dlz/auth/password-reset/password-reset-schemas';
import { StepHeading } from '@eliadmin/core/src/components/dlz/auth/password-reset/step-heading';
import { useRequestError } from '@eliadmin/core/src/components/dlz/auth/password-reset/use-request-error';
import { FormRow } from '@eliadmin/core/src/components/dlz/forms/form-row';
import { PasswordInput } from '@eliadmin/core/src/components/dlz/forms/password-input';
import { PasswordStrength } from '@eliadmin/core/src/components/dlz/forms/password-strength';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Form, FormField } from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

const PASSWORD_FIELDS = ['password', 'password_confirmation'] as const;
const PASSWORD_AND_EMAIL_FIELDS = ['email', ...PASSWORD_FIELDS] as const;

/**
 * Step 3 – „Nastavte si nové heslo“ (Figma 60158:20811, mobile 60438:82355). The e-mail comes from
 * the link (`?email=`); without it the step asks for it, so the request stays valid.
 */
export function ResetStep({
  email,
  token,
  onDone,
}: {
  email?: string;
  token?: string;
  onDone: (loggedOutOtherDevices: boolean) => void;
}) {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const logoutId = useId();
  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema(formValidationMessages)),
    defaultValues: {
      email: email ?? '',
      password: '',
      password_confirmation: '',
      logout_other_devices: true,
    },
  });
  const password = useWatch({ control: form.control, name: 'password' });
  const { alert, reportError, clearAlert } = useRequestError(
    form,
    email ? PASSWORD_FIELDS : PASSWORD_AND_EMAIL_FIELDS,
  );

  const submit = form.handleSubmit(async values => {
    clearAlert();
    if (!token) {
      reportError(undefined, t('text3140'));
      return;
    }
    try {
      await resetPassword({ ...values, token, locale });
      onDone(values.logout_other_devices);
    } catch (error) {
      reportError(error, t('text3140'));
    }
  });

  return (
    <Form {...form}>
      <form noValidate onSubmit={submit} className="flex w-full flex-col items-center gap-6">
        <StepHeading
          icon="key"
          iconSize={38.4}
          title={RESET_COPY.reset.title}
          description={RESET_COPY.reset.text(email)}
        />
        {email ? (
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
        ) : (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormRow label={RESET_COPY.reset.emailLabel} required className="w-full">
                <Input
                  {...field}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  data-autofocus=""
                />
              </FormRow>
            )}
          />
        )}
        <div className="flex w-full flex-col gap-2">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormRow label={RESET_COPY.reset.password} required>
                <PasswordInput
                  {...field}
                  autoComplete="new-password"
                  data-autofocus={email ? '' : undefined}
                />
              </FormRow>
            )}
          />
          <PasswordStrength value={password} />
        </div>
        <FormField
          control={form.control}
          name="password_confirmation"
          render={({ field }) => (
            <FormRow label={RESET_COPY.reset.repeat} required className="w-full">
              <PasswordInput {...field} autoComplete="new-password" />
            </FormRow>
          )}
        />
        <FormField
          control={form.control}
          name="logout_other_devices"
          render={({ field }) => (
            <div className="flex w-full items-center gap-3">
              <Checkbox
                id={logoutId}
                ref={field.ref}
                name={field.name}
                checked={field.value}
                onCheckedChange={checked => field.onChange(checked === true)}
                onBlur={field.onBlur}
              />
              <label
                htmlFor={logoutId}
                className="dlz-body-m cursor-pointer text-left text-[#0F1012]"
              >
                {RESET_COPY.reset.logoutOthers}
              </label>
            </div>
          )}
        />
        <FormAlert>{alert}</FormAlert>
        <Button
          type="submit"
          isLoading={form.formState.isSubmitting}
          className="h-12 w-full md:h-[52px] md:w-auto"
        >
          {RESET_COPY.reset.submit}
        </Button>
      </form>
    </Form>
  );
}
